import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import PlaceholderShimmer from '@/components/common/PlaceholderShimmer.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import CourseApi from '@/services/api/CourseApi'
  import { CourseModule } from '@/store/modules/course'
  import useCommonMixin from '@/composables/useCommonMixin'
  import { UserModule } from '@/store/modules/user'
  import useSegment from '@/composables/useSegment'
  import { useRoute, useRouter } from 'vue-router/composables'
  import type { CourseItem, CoursePackage } from '@/services/interfaces/Course'
  import TextRenderer from '@/components/editor/TextRenderer.vue'
  import useI18n from '@/composables/useI18n'
  import CookieHelper from '@/helpers/CookieHelper'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CourseTrialPage',
  setup(__props) {

  const route = useRoute()
  const router = useRouter()
  const { zigZagIcon, checkboxLight, checkedCheckboxSquare, checkmark, arrowRightIndicator, arrowDown } = useIcons()
  const { isDesktopDevice } = useBreakpoint()
  const {
    nextOnboardingPage,
    isDevCompany,
    joinArrayOfStringsWithNewlineCharacter,
    setIsLoadingComponentViewData,
    highlightedCourseBundlePrefix,
    desktopLogo,
    isTauresCompany,
  } = useCommonMixin()
  const { pageViewed, trackPage, frontendCourseTrialStarted } = useSegment()
  const { translateString } = useI18n()

  const submittingTrials = ref(false)
  const showOtherBundles = ref(true)

  const selectedCourses = ref([] as { courseId: string; courseName: string }[])

  // we are hardcoding the order for now
  let preferredOrderedCourseBundles = [
    {
      id: '81d1fa5a-4be3-434c-bf2d-f50030c6c5c5',
      courses: ['63d7d5bb-e8e9-429d-b360-cefdecd80448', '489dd660-9a78-4f94-8076-1e8f20a451e2'],
    },
    {
      id: 'cd8018c0-ab72-4d94-a3e5-001a498f1640',
      courses: ['8de44221-a055-4cdb-8ce5-d7ac148cb49e', 'ef7607dd-bb67-42f7-b0f8-a0f1a437fdd9'],
    },
    {
      id: '73e5ef94-2e97-470f-b445-6c885dce7baa',
      courses: ['84be496a-b904-4184-b456-98736fea9259', '284424aa-1a0e-4c60-a5e0-d8df6d9d1868'],
    },
    {
      id: '60dde532-73c3-4b11-8c6f-13f1bba92c1c',
      courses: ['4dc07be7-7aab-43a0-ae70-4b5f00b5a79e', 'd1643bae-db0b-4778-9662-7080f21ea5e4'],
    },
  ]

  const filterBundles = (bundles: CoursePackage[]) => {
    return bundles.filter((bundle) => bundle.courseBundleId !== '31aa50e8-1c38-47d6-a487-d94507a9dbf2')
  }

  const orderCourses = (bundle: CoursePackage, preferredBundle: { id: string; courses: string[] }) => {
    bundle.courses?.sort((a, b) => {
      return preferredBundle.courses?.indexOf(a.id) - preferredBundle.courses?.indexOf(b.id)
    })
  }

  const getOrderedBundles = (filteredBundles: CoursePackage[]) => {
    return preferredOrderedCourseBundles.map((preferredBundle) => {
      const actualBundle = filteredBundles.find((bundle) => bundle.courseBundleId === preferredBundle.id)
      if (actualBundle) {
        orderCourses(actualBundle, preferredBundle)
      }
      return actualBundle
    })
  }

  const sortBundlesByPrefix = (bundles: CoursePackage[]) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return bundles.sort((a, _b) => {
      if (a?.title.includes(highlightedCourseBundlePrefix.value!)) return -1
      return 1
    })
  }

  const loadingPackages = computed(() => CourseModule.courseBundles === null)

  const courseBundles = computed(() => {
    if (isDevCompany.value) return CourseModule.courseBundles
    if (!CourseModule.courseBundles) return []

    if (isTauresCompany.value) {
      // remove AEVO bundle
      preferredOrderedCourseBundles = preferredOrderedCourseBundles.filter(
        (bundle) => bundle.id !== '60dde532-73c3-4b11-8c6f-13f1bba92c1c',
      )
    }

    const filteredBundles = filterBundles(CourseModule.courseBundles)
    const preOrderedBundles = getOrderedBundles(filteredBundles)

    if (!highlightedCourseBundlePrefix.value) return preOrderedBundles

    let sortedBasedOnPrefix: CoursePackage[] = []

    if (preOrderedBundles && preOrderedBundles.length) {
      sortedBasedOnPrefix = sortBundlesByPrefix(preOrderedBundles as CoursePackage[])
    }

    return showOtherBundles.value ? sortedBasedOnPrefix : [sortedBasedOnPrefix[0]]
  })

  const stringifiedBundleBenefits = computed(() => (bundleBenefits: string[]) => {
    return joinArrayOfStringsWithNewlineCharacter(bundleBenefits)
  })

  const courseIsSelected = computed(() => {
    return (courseId: string) => {
      return selectedCourses.value.some((c) => c.courseId === courseId)
    }
  })

  const anyCourseInBundleIsSelected = computed(
    () => (courses?: CourseItem[]) => courses?.some((course) => courseIsSelected.value(course.id)),
  )

  const packageIsSelected = computed(() => {
    return (courses?: CourseItem[]) => {
      return courses?.every((course) => {
        return courseIsSelected.value(course.id)
      })
    }
  })

  const bundleHasBenefits = computed(
    () => (bundle?: CoursePackage) => !!bundle?.bundleBenefits && bundle?.bundleBenefits?.length,
  )

  const showMoreOrLessPackagesText = computed(() =>
    showOtherBundles.value
      ? translateString('onboarding.trialSelector.showLessPackages')
      : translateString('onboarding.trialSelector.chooseOtherPackages'),
  )

  const toggleSelectCourse = (course?: CourseItem) => {
    if (!course || course.state !== 'ready') return

    const isSelected = courseIsSelected.value(course.id)

    isSelected ? unselectCourse(course) : selectCourse(course)
  }

  const selectCourse = (course: CourseItem) => {
    selectedCourses.value.push({ courseId: course.id, courseName: course.title })
  }

  const unselectCourse = (course: CourseItem) => {
    selectedCourses.value = selectedCourses.value.filter((c) => c.courseId !== course.id)
  }

  const selectCompletePackage = (courses?: CourseItem[]) => {
    const allAreSelected = courses?.every((course) => courseIsSelected.value(course.id))

    courses?.forEach((course) => {
      if (allAreSelected || !courseIsSelected.value(course.id)) {
        toggleSelectCourse(course)
      }
    })
  }

  const submitTrials = () => {
    submittingTrials.value = true

    const courseIds = selectedCourses.value.map((c) => c.courseId)
    if (!courseIds.length) return

    CourseApi.startCourseTrial(courseIds)
      .then(() => {
        CookieHelper.removeCrossDomainCookie('bundlePrefix')
        courseIds.forEach((id) => {
          const courseName = selectedCourses.value.find((s) => s.courseId === id)!.courseName
          frontendCourseTrialStarted(UserModule.email, id, courseName)
        })
      })
      .finally(() => {
        submittingTrials.value = false
        goToNextOnboardingPage()
      })
  }

  const goToNextOnboardingPage = () => {
    if (
      nextOnboardingPage.value('CourseTrialPage') &&
      nextOnboardingPage.value('CourseTrialPage') !== 'ExamDatePicker'
    ) {
      if (nextOnboardingPage.value('CourseTrialPage') !== route.name) {
        router.push({ name: nextOnboardingPage.value('CourseTrialPage') }).catch(() => {
          return
        })
      }
    } else {
      window.location.href = '/'
    }
  }

  watch(loadingPackages, () => {
    preSelectBundles()
  })

  const preSelectBundles = () => {
    if (!CourseModule.courseBundles) return

    const bundle = courseBundles.value?.[0]

    if (highlightedCourseBundlePrefix.value && bundle) {
      showOtherBundles.value = false

      bundle.courses?.forEach((course) => {
        toggleSelectCourse(course)
      })
    }
  }

  onMounted(() => {
    preSelectBundles()

    if (UserModule.hasValidUser) {
      if (UserModule.hasSelectedTrial) return goToNextOnboardingPage()
      trackPage('Course Trial Page')
      pageViewed('Course Trial Page')
    }

    setIsLoadingComponentViewData(false)
  })

return { __sfc: true,route, router, zigZagIcon, checkboxLight, checkedCheckboxSquare, checkmark, arrowRightIndicator, arrowDown, isDesktopDevice, nextOnboardingPage, isDevCompany, joinArrayOfStringsWithNewlineCharacter, setIsLoadingComponentViewData, highlightedCourseBundlePrefix, desktopLogo, isTauresCompany, pageViewed, trackPage, frontendCourseTrialStarted, translateString, submittingTrials, showOtherBundles, selectedCourses, preferredOrderedCourseBundles, filterBundles, orderCourses, getOrderedBundles, sortBundlesByPrefix, loadingPackages, courseBundles, stringifiedBundleBenefits, courseIsSelected, anyCourseInBundleIsSelected, packageIsSelected, bundleHasBenefits, showMoreOrLessPackagesText, toggleSelectCourse, selectCourse, unselectCourse, selectCompletePackage, submitTrials, goToNextOnboardingPage, preSelectBundles, computed, onMounted, ref, watch, SVGRenderer, PlaceholderShimmer, KetchUpButton, LoadingSpinner, useIcons, useBreakpoint, CourseApi, CourseModule, useCommonMixin, UserModule, useSegment, useRoute, useRouter, TextRenderer, useI18n, CookieHelper }
}

})